import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Brain, Check, Loader2, RefreshCw, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { kursbestandAPI } from '@/lib/api';

/* Kursbestand lernt aus den Check-ins (13.10.2026): Vorschläge, welcher Kurs ein Stammkurs ist — aus den letzten acht
   Wochen, täglich neu gerechnet. Der Lauf ändert nichts von selbst; erst „Übernehmen" trägt den Kurs ein. */
const tagDe = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
const fehler = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export default function KursbestandVorschlaege({ leitung, onGeaendert }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [alle, setAlle] = useState(false);

  const lade = useCallback(async (neu) => {
    if (neu) setBusy('lauf');
    try { setD((await kursbestandAPI.vorschlaege(neu)).data); }
    catch (e) { if (neu) toast.error(fehler(e, 'Vorschläge nicht geladen')); }
    finally { if (neu) setBusy(null); }
  }, []);
  useEffect(() => { lade(false); }, [lade]);

  const entscheiden = async (v, annehmen) => {
    setBusy(v.id);
    try {
      await kursbestandAPI.vorschlagEntscheiden(v.id, annehmen);
      toast.success(annehmen ? `${v.member_name}: ${v.art === 'entfernen' ? 'Kurs ausgetragen' : 'Kurs eingetragen'}` : 'Abgelehnt — kommt acht Wochen nicht wieder');
      await lade(false); if (annehmen) onGeaendert?.();
    } catch (e) { toast.error(fehler(e, 'Das hat nicht geklappt')); lade(false); }
    setBusy(null);
  };
  const sichere = async () => {
    setBusy('sichere');
    try { const r = await kursbestandAPI.vorschlaegeSichere(); toast.success(`${r.data.angenommen} sichere Vorschläge übernommen`); await lade(false); onGeaendert?.(); }
    catch (e) { toast.error(fehler(e, 'Das hat nicht geklappt')); }
    setBusy(null);
  };

  if (!d) return null;
  const g = d.gelernt;
  const liste = alle ? d.vorschlaege : d.vorschlaege.slice(0, 8);
  return (
    <Card className="border-border/60" data-testid="kursbestand-vorschlaege">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <Brain className="h-4 w-4 text-marke" /> Vorschläge aus Buchungen und Check-ins <Badge variant="secondary">{d.vorschlaege.length}</Badge>
          <span className="ml-auto flex gap-2">
            {leitung && d.sicher > 0 && <Button size="sm" disabled={!!busy} onClick={sichere} data-testid="kv-sichere">{busy === 'sichere' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{d.sicher} sichere übernehmen</Button>}
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => lade(true)} className="gap-1.5" title="Jetzt neu rechnen">{busy === 'lauf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Neu rechnen</Button>
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Wer in mindestens {g.schwelle} der letzten {g.wochen_fenster} Wochen denselben Kurs gebucht oder zu seiner Zeit eingecheckt hat, wird als Stammkunde vorgeschlagen. „Sicher“ heißt ab {g.sicher_ab} Wochen.
          Der Lauf rechnet täglich neu{g.letzter_lauf ? `, zuletzt am ${new Date(g.letzter_lauf).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}` : ''}.
          {g.entschieden > 0 && ` Bisher ${g.angenommen} von ${g.entschieden} Vorschlägen übernommen (${g.quote} %).`}
          {g.grund && ` Gelernt: ${g.grund}.`}
        </p>
      </CardHeader>
      <CardContent>
        {d.vorschlaege.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine offenen Vorschläge. {g.letzter_lauf ? 'Sobald jemand regelmäßig zu einer Kurszeit kommt, steht er hier.' : 'Mit „Neu rechnen“ startet der erste Lauf.'}</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {liste.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5 text-sm" data-testid={`kv-${v.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    <Link to={`/leads/${v.member_id}`} className="hover:underline">{v.member_name || 'Ohne Namen'}</Link>
                    <span className="mx-1.5 text-muted-foreground">{v.art === 'entfernen' ? 'raus aus' : '→'}</span>
                    {v.kurs_name} <span className="font-normal text-muted-foreground">{(v.kurs_tag || '').slice(0, 2)} {v.kurs_start}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {v.art === 'entfernen'
                      ? `Stammkurs, aber in ${g.wochen_fenster} Wochen kein Besuch dort. Kommt sonst regelmäßig (${v.wochen_gesamt} Wochen mit Check-in).`
                      : `in ${v.wochen} von ${g.wochen_fenster} Wochen dabei · ${[v.buchungen > 0 ? `${v.buchungen}× gebucht` : '', v.besuche > 0 ? `${v.besuche}× eingecheckt` : ''].filter(Boolean).join(', ')} · zuletzt ${tagDe(v.letzte)}`}
                  </p>
                </div>
                {v.art === 'hinzufuegen' && <Badge variant={v.sicherheit === 'hoch' ? 'default' : 'outline'} className="text-[10px]">{v.sicherheit === 'hoch' ? 'sicher' : 'knapp'}</Badge>}
                <span className="flex gap-1.5">
                  <Button size="sm" className="h-8 gap-1" disabled={!!busy} onClick={() => entscheiden(v, true)}>{busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Übernehmen</Button>
                  <Button size="sm" variant="ghost" className="h-8 gap-1" disabled={!!busy} onClick={() => entscheiden(v, false)}><X className="h-3.5 w-3.5" /> Ablehnen</Button>
                </span>
              </li>
            ))}
          </ul>
        )}
        {d.vorschlaege.length > 8 && <button type="button" className="mt-2 text-xs underline text-muted-foreground hover:text-foreground" onClick={() => setAlle((x) => !x)}>{alle ? 'Weniger zeigen' : `Alle ${d.vorschlaege.length} zeigen`}</button>}
      </CardContent>
    </Card>
  );
}
